import type { ParsingOptions, WorkBook, WorkSheet, XLSX$Utils } from 'xlsx';

interface XlsxLib {
  read: (data: unknown, opts?: ParsingOptions) => WorkBook;
  utils: XLSX$Utils;
}

export class XlsFile {
  static xlsx: XlsxLib | null = null;

  #workbook: WorkBook | null = null;
  #rows: Record<string, string>[] = [];
  #columns: string[] = [];

  static async parse(file: File) {
    const xlsxFile = new XlsFile();

    if (!XlsFile.xlsx) {
      XlsFile.xlsx = await import('xlsx');
    }

    await xlsxFile.#extractWorkbook(file);
    xlsxFile.#extractData();
    xlsxFile.#extractColumns();

    return xlsxFile;
  }

  get rows() {
    return this.#rows;
  }

  get columns() {
    return this.#columns;
  }

  removeColumns(columnsToRemove: string[]) {
    this.#columns = this.#columns.filter(column => !columnsToRemove.includes(column));
  }

  sortColumns(sortFn: (a: string, b: string) => number) {
    this.#columns.sort(sortFn);
  }

  async #extractWorkbook(file: File) {
    if (!XlsFile.xlsx) return;
    if (/text\/.*/i.exec(file.type)) {
      this.#workbook = XlsFile.xlsx.read(await file.text(), { type: 'string' });
    } else {
      this.#workbook = XlsFile.xlsx.read(await file.arrayBuffer(), { type: 'buffer' });
    }
  }

  #extractData() {
    if (!XlsFile.xlsx) return;

    const sheetName = this.#workbook?.SheetNames[0];
    if (sheetName) {
      const sheet = this.#workbook?.Sheets[sheetName];
      if (sheet) {
        if (this.#hasHeader(sheet)) {
          this.#rows = XlsFile.xlsx.utils.sheet_to_json(sheet, { defval: null });
        } else {
          this.#rows = XlsFile.xlsx.utils.sheet_to_json(sheet, { header: 'A', defval: null });
        }
      }
    }
  }

  #extractColumns() {
    if (this.#rows[0]) {
      this.#columns = Object.keys(this.#rows[0]);
    }
  }

  #hasHeader(sheet: WorkSheet) {
    if (!XlsFile.xlsx) return false;
    const json = XlsFile.xlsx.utils.sheet_to_json<unknown[]>(sheet, { header: 1 });
    return json[0] ? isHeader(json[0]) : false;
  }
}

function isHeader(row: unknown[]) {
  const normalizedRow = row.filter((cell): cell is string => typeof cell === 'string');
  return (
    normalizedRow.length === row.length &&
    normalizedRow.every(cell => !isParsableDate(cell)) &&
    new Set(normalizedRow).size === normalizedRow.length
  );
}

function isParsableDate(value: unknown) {
  if (typeof value !== 'string') return false;
  return isNaN(Number(value)) && !isNaN(Date.parse(value));
}
